import React, { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';

import { getStateHeroImages } from 'config/stateHeroImages';
import { Picture } from 'components/core/Picture';
import { H1, Paragraph } from 'components/core/Typography';
import { SearchBox } from 'components/search/SearchBox';
import MapPin from 'components/svg/MapPin';
import { withSearchHandler } from 'decorators';

import './StateHero.scss';

/**
 * StateHero - Responsive hero section with state-specific background and search
 */
function StateHeroBase({ location, stateNickname, className, handleSearch }) {
	const { state, fullState } = location;
	const images = useMemo(() => getStateHeroImages(state), [state]);
	const altText = `${fullState} - ${stateNickname}`;
	const hasImages = images !== null;

	return (
		<div className={`state-hero${className ? ` ${className}` : ''}`}>
			<div className="state-hero__wrapper">
				<div
					className={`state-hero__image-container${!hasImages ? ' state-hero__image-container--gradient' : ''}`}
				>
					{hasImages ? (
						<Picture
							src={images.desktop}
							alt={altText}
							className="state-hero__image"
							pictureClassName="state-hero__picture"
							sources={[
								{
									media: '(max-width: 767px)',
									srcSet: images.mobile,
									type: 'image/webp',
								},
								{
									media: '(min-width: 768px)',
									srcSet: images.desktop,
									type: 'image/webp',
								},
							]}
							loading="eager"
							fetchPriority="high"
						/>
					) : null}
				</div>

				<div className="state-hero__card">
					<H1 className="state-hero__headline">
						Find Self Storage in <br />
						{fullState}
					</H1>

					<Paragraph className="state-hero__subheading">
						Make a free reservation in minutes!
					</Paragraph>
					<SearchBox
						onSearch={handleSearch}
						icon={<MapPin fill="#146FB4" />}
						id="state-hero-search"
						inputLabel="Enter ZIP Code or City Name"
						useBrowserLocation
						ariaLabel={`Search for storage in ${fullState}`}
					/>
				</div>
			</div>
		</div>
	);
}

StateHeroBase.propTypes = {
	location: PropTypes.shape({
		state: PropTypes.string.isRequired,
		fullState: PropTypes.string.isRequired,
	}).isRequired,
	stateNickname: PropTypes.string.isRequired,
	className: PropTypes.string,
	handleSearch: PropTypes.func,
};

const StateHeroComponent = memo(StateHeroBase);

export { StateHeroBase as StateHeroComponent };

export default compose(withSearchHandler)(StateHeroComponent);
